import { Link } from "wouter";
import { ArrowRight, Check, MapPin, Clock, Truck, Shield, Zap, Target } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead, { createLocalBusinessSchema } from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES, CONTACT } from "@/lib/constants";
import LazyImage from "@/components/LazyImage";

const localBenefits = [
  {
    icon: Truck,
    title: "Snabb leverans till Örebro",
    text: "Vi planerar leveransen från Eskilstuna till Örebro utifrån detaljernas omfattning, önskat datum och vald transportlösning.",
  },
  {
    icon: Clock,
    title: "Korta ledtider för industrin",
    text: "Med tre CO2-lasrar och hög kapacitet kan vi hantera brådskande jobb från Örebros tillverknings- och logistikföretag.",
  },
  {
    icon: Shield,
    title: "Garanterad precision",
    text: "Toleranser ner till 0,1 mm med moderna Trumpf- och Amada CO2-lasrar. Första bit-kontroll vid varje projektstart.",
  },
  {
    icon: Target,
    title: "Helhetslösning nära dig",
    text: "Kombinera laserskärning med kantpressning och svetsning. En komplett leverantör ett stenkast från Örebro.",
  },
];

const orebroIndustries = [
  {
    name: "Tillverkning & Verkstad",
    desc: "Vi är en pålitlig partner för Örebroregionens tillverknings-, verkstads- och maskinindustri.",
  },
  {
    name: "Logistik & Transport",
    desc: "Komponenter till transportutrustning och lagerlösningar för det expansiva logistiknavet i Örebroregionen.",
  },
  {
    name: "Automation & Robotik",
    desc: "Prototyper och serietillverkning för automationsföretag. Hög precision för känsliga applikationer.",
  },
  {
    name: "Entreprenad & Bygg",
    desc: "Skräddarsydda detaljer för bygg- och anläggningsprojekt i en växande region. Från balkar till fasadelement.",
  },
];

const capabilities = [
  { material: "Svart stål", maxThickness: "25 mm", minThickness: "0,5 mm" },
  { material: "Rostfritt stål", maxThickness: "20 mm", minThickness: "0,5 mm" },
  { material: "Aluminium", maxThickness: "15 mm", minThickness: "0,5 mm" },
  { material: "Höghållfasthetsstål (Strenx)", maxThickness: "20 mm", minThickness: "1 mm" },
  { material: "Slitstål (Hardox/Raex)", maxThickness: "20 mm", minThickness: "1 mm" },
];

export default function LaserskarningOrebro() {
  const localServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Laserskärning Örebro",
    description:
      "Professionell laserskärning för företag i Örebro. ASPAB erbjuder CO2 Laser-skärning med toleranser ner till 0,1 mm, snabba leveranser och brett materialutbud.",
    provider: {
      "@type": "LocalBusiness",
      name: "ASPAB",
      telephone: "+46161326 00",
      email: "order@aspab.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Abramsons väg 2",
        addressLocality: "Eskilstuna",
        postalCode: "632 39",
        addressCountry: "SE",
      },
    },
    areaServed: {
      "@type": "City",
      name: "Örebro",
      containedInPlace: {
        "@type": "Country",
        name: "Sweden",
      },
    },
    serviceType: "Laserskärning",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Laserskärningstjänster",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning stål",
            description: "Laserskärning i svart stål upp till 25 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning rostfritt",
            description: "Laserskärning i rostfritt stål upp till 20 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning aluminium",
            description: "Laserskärning i aluminium upp till 15 mm tjocklek",
          },
        },
      ],
    },
  };

  const localBusinessSchema = createLocalBusinessSchema({
    areaServed: "Örebro",
    serviceDescription: "Professionell laserskärning, kantpressning och svetsning för företag i Örebro med omnejd. Leveransplan bekräftas i offerten.",
  });

  return (
    <Layout>
      <SEOHead
        title="Laserskärning Örebro – precision från Eskilstuna | ASPAB"
        description="Laserskärning för Örebros industri från ASPAB i Eskilstuna. Stål, rostfritt och aluminium med toleranser ner till 0,1 mm. Begär offert."
        canonical="https://aspab.com/laserskarning-orebro"
        path="/laserskarning-orebro"
        keywords="laserskärning Örebro, plåtbearbetning Örebro, metallbearbetning Örebro, tillverkningsindustri Örebro"
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Örebro", href: "/laserskarning-orebro" },
        ]}
        structuredData={[localServiceSchema, localBusinessSchema]}
        ogImage={IMAGES.ogOrebro}
      />

      <PageHero
        title="Laserskärning Örebro"
        subtitle="Din partner för laserskärning i Örebro med planerad leverans, hög precision och kapacitet för regionens industri."
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Örebro", href: "/laserskarning-orebro" },
        ]}
        image={IMAGES.heroLaser}
      />

      {/* Intro – Varför välja ASPAB från Örebro */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Laserskärning för Örebroregionen
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Lokal närvaro, industriell kapacitet
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  För företag i Örebro är ASPAB i Eskilstuna en tillgänglig partner för laserskärning. Vi kombinerar planerade vägtransporter med en specialiserad maskinpark och praktisk produktionserfarenhet.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Vi förstår kraven inom Örebroregionens tillverknings-, verkstads- och maskinindustri.
                  Vår kapacitet är anpassad för att möta behoven hos både tillverknings- och logistikföretag i regionen.
                </p>
                <div className="flex items-center gap-3 mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <MapPin className="w-5 h-5 text-[#2563EB] shrink-0" />
                  <div>
                    <span className="text-gray-900 font-medium text-sm">Leverans från ASPAB i Eskilstuna till Örebro</span>
                    <span className="text-gray-500 text-sm block">Transportupplägg bekräftas i offerten</span>
                  </div>
                </div>
                <Link
                  href="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors mt-6"
                >
                  Begär offert – få snabb återkoppling <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="space-y-4">
                <LazyImage
                  src={IMAGES.laserOldSite}
                  alt="Laserskärning för företag i Örebro – ASPAB CO2 Laser"
                  className="w-full rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">0,1 mm</div>
                    <div className="text-gray-500 text-sm mt-1">Tolerans</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">&lt;24h</div>
                    <div className="text-gray-500 text-sm mt-1">Leverans enkla detaljer</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">25 mm</div>
                    <div className="text-gray-500 text-sm mt-1">Max tjocklek stål</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">30+</div>
                    <div className="text-gray-500 text-sm mt-1">Års erfarenhet</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Fördelar med ASPAB för Örebroföretag */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Fördelar
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Varför Örebros industri väljer ASPAB
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Vår geografiska närhet, kombinerat med hög teknisk kapacitet, gör oss till det självklara valet för många företag i Örebro.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6">
            {localBenefits.map((benefit, i) => (
              <AnimatedSection key={benefit.title} delay={i * 120}>
                <div className="bg-white border border-gray-200 rounded-lg p-6 h-full hover:border-[#2563EB]/20 hover:shadow-md transition-all duration-300">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 shrink-0 flex items-center justify-center bg-[#2563EB]/10 rounded-lg">
                      <benefit.icon className="w-6 h-6 text-[#2563EB]" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-gray-900">{benefit.title}</h3>
                      <p className="text-gray-500 text-sm mt-2 leading-relaxed">{benefit.text}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Branscher i Örebro */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Branscher vi betjänar
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Laserskärning för Örebros industrier
                </h2>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Örebro är ett nav för tillverkning och logistik. Vi har anpassat våra tjänster för att möta de specifika behoven hos regionens nyckelindustrier.
                </p>
                <div className="mt-8 space-y-5">
                  {orebroIndustries.map((ind) => (
                    <div key={ind.name} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#2563EB] shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-gray-900 font-semibold">{ind.name}</h3>
                        <p className="text-gray-500 text-sm mt-0.5">{ind.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div className="overflow-hidden rounded-lg">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full aspect-video object-cover rounded-lg"
                  poster={IMAGES.heroLaser}
                >
                  <source src={IMAGES.laserVideo} type="video/mp4" />
                </video>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Tekniska specifikationer */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-12">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Kapacitet
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Material och tjocklekar vi hanterar
              </h2>
              <p className="text-gray-500 mt-4 leading-relaxed">
                Med fyra moderna lasermaskiner från Trumpf och Amada har vi kapacitet att skära i ett brett
                utbud av material. Alla material levereras med garanterade toleranser på 0,1 mm.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-4 px-6 text-gray-500 text-sm font-medium">Material</th>
                    <th className="text-left py-4 px-6 text-gray-500 text-sm font-medium">Min tjocklek</th>
                    <th className="text-left py-4 px-6 text-gray-500 text-sm font-medium">Max tjocklek</th>
                    <th className="text-left py-4 px-6 text-gray-500 text-sm font-medium">Tolerans</th>
                  </tr>
                </thead>
                <tbody>
                  {capabilities.map((cap) => (
                    <tr key={cap.material} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 text-gray-900 font-medium">{cap.material}</td>
                      <td className="py-4 px-6 text-gray-500">{cap.minThickness}</td>
                      <td className="py-4 px-6 text-gray-500">{cap.maxThickness}</td>
                      <td className="py-4 px-6 text-[#2563EB] font-medium">0,1 mm</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Process */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-12">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Så fungerar det
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Beställ laserskärning från Örebro – enkelt och smidigt
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: "01",
                title: "Skicka förfrågan",
                desc: "Ladda upp din CAD-fil, PDF eller skiss via vårt kontaktformulär. Vi tar emot alla vanliga filformat.",
              },
              {
                step: "02",
                title: "Snabb offert",
                desc: "Vi granskar underlaget och återkommer med offert, materialrekommendation och leveranstid.",
              },
              {
                step: "03",
                title: "Produktion",
                desc: "Skärning med CO2 Laser, med första bit-kontroll och löpande kvalitetskontroll under hela produktionen.",
              },
              {
                step: "04",
                title: "Leverans till Örebro",
                desc: "Leverans direkt till din adress i Örebro. Enkla detaljer ofta samma dag tack vare det korta avståndet.",
              },
            ].map((item, i) => (
              <AnimatedSection key={item.step} delay={i * 120}>
                <div className="bg-white border border-gray-200 rounded-lg p-6 h-full hover:border-[#2563EB]/20 transition-colors">
                  <div className="font-heading text-3xl font-bold text-[#2563EB]/20">{item.step}</div>
                  <h3 className="font-heading text-lg font-semibold text-gray-900 mt-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm mt-2 leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Relaterade tjänster */}
      <section className="bg-gray-50 py-16 md:py-20 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Komplett metallbearbetning
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mt-3">
                Mer än bara laserskärning
              </h2>
              <p className="text-gray-500 mt-3 leading-relaxed">
                Utöver laserskärning erbjuder vi kantpressning och svetsning – allt under ett tak i Eskilstuna.
                Få färdiga komponenter levererade direkt till Örebro.
              </p>
            </div>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Laserskärning",
                desc: "Tre CO2-lasrar för stål, rostfritt och aluminium med toleranser ner till 0,1 mm.",
                href: "/tjanster/laserskarning",
              },
              {
                title: "Kantpressning",
                desc: "Tre kantpressar upp till 310 ton för exakt bockning av plåt.",
                href: "/tjanster/kantpressning",
              },
              {
                title: "Svetsning",
                desc: "Svetsning av stål, rostfritt och aluminium för hållbara konstruktioner.",
                href: "/tjanster/svetsning",
              },
            ].map((svc, i) => (
              <AnimatedSection key={svc.title} delay={i * 100}>
                <Link href={svc.href} className="group block">
                  <div className="bg-white border border-gray-200 rounded-lg p-6 h-full hover:border-[#2563EB]/30 hover:shadow-md transition-all duration-300">
                    <h3 className="font-heading text-lg font-semibold text-gray-900 group-hover:text-[#2563EB] transition-colors">
                      {svc.title}
                    </h3>
                    <p className="text-gray-500 text-sm mt-2 leading-relaxed">{svc.desc}</p>
                    <span className="inline-flex items-center gap-1 text-[#2563EB] text-sm font-medium mt-4 group-hover:gap-2 transition-all">
                      Läs mer <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#2563EB] py-16 md:py-20">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
                Behöver du laserskärning i Örebro?
              </h2>
              <p className="text-white mt-4 text-lg leading-relaxed">
                Kontakta oss idag för en kostnadsfri offert. Vi levererar precisionsdetaljer till
                hela Örebroregionen med snabba ledtider och konkurrenskraftiga priser.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <Link
                  href="/kontakt"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#2563EB] font-semibold rounded-md hover:bg-gray-100 transition-colors"
                >
                  Begär offert <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 border border-white/20 text-white font-medium rounded-md hover:bg-white/20 transition-colors"
                >
                  Ring {CONTACT.phone}
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
